<template>
  <div class="color_picker_panel" @click.stop>
    <div class="color_hd" :class="!(showClose||titleConfig.show)&&!titleConfig.text?'color_hd_0':''">
      <div class="title" v-if="titleConfig.show!==false">
        <span :style="titleConfig.style || {}">{{ titleConfig.text }}</span>
        <span v-if="showClose" class="close_box" @click="handleClose">×</span>
      </div>

      <!-- 模式切换按钮 -->
      <div class="mode_switch">
        <button
          :class="['mode_btn', { active: currentMode === 'linear' }]"
          @click="switchMode('linear')"
        >
          纯色
        </button>
        <button
          :class="['mode_btn', { active: currentMode === 'gradient' }]"
          @click="switchMode('gradient')"
        >
          渐变
        </button>
      </div>

      <div class="gcolor" v-if="currentMode === 'gradient'">
        <div class="gcolor_deg" v-if="!disabledColorDeg">
          <div class="gcolor_deg_span">角度</div>
          <Slider v-model="deg" :min="0" :max="360" :show-tooltip="false" />
          <input class="number_input" v-model="deg" />
        </div>

        <div class="gcolor_bar" ref="refColorBar">
          <div
            class="gcolor_bar_bg"
            :style="`background: ${barStyle}`"
            @click="handlePotBar"
          ></div>
          <div class="gcolor_bar_pot_box">
            <div
              class="gcolor_bar_pot"
              v-for="(item, index) in colors"
              :key="`${item.pst}_${index}`"
              :style="{
                left: getBarPst(item.pst),
              }"
              :class="{
                on: selectIndex === index,
              }"
              @mousedown="sliderPotDown(index)"
              @click="clickGColorPot(index)"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <div class="gradient_box" v-if="currentMode === 'gradient'">
      <template v-for="(item, index) in colors" :key="`${item.pst}_${index}`">
        <ColorPicker
          theme="light"
          :color="item.color"
          :sucker-hide="true"
          @changeColor="changeColor"
          v-if="index === selectIndex"
        />
      </template>
    </div>

    <div class="linear" v-else>
      <ColorPicker
        v-if="color"
        theme="light"
        :color="color.color"
        :sucker-hide="true"
        @changeColor="changeColor"
      />
    </div>
  </div>
</template>

<script>
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  toRefs,
  watch,
} from 'vue'
import { ColorPicker } from 'vue-color-kit'
import ColorScale from 'color-scales'
import { cloneDeep, keepDecimal } from '../utils/index'
import useDraggle from '../hooks/useDraggle'
import Slider from 'element-plus/lib/components/slider'
import 'element-plus/es/components/slider/style/css'

export default {
  name: 'ColorPickerPanel',
  components: {
    ColorPicker,
    Slider,
  },
  props: {
    type: {
      type: String,
      default: 'linear',
    },
    disabledColorDeg: {
      type: Boolean,
      default: false,
    },
    pDeg: {
      type: Number,
      default: 90,
    },
    pColor: {
      type: Object,
      default: null,
    },
    pColors: {
      type: Array,
      default: null,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    titleConfig: {
      type: Object,
      default() {
        return {
          text: '颜色选择器',
          style: {},
        }
      },
    },
  },
  emits: ['change', 'close'],
  setup(props, { emit }) {
    const { type, pColors, pDeg, pColor } = toRefs(props)
    const state = reactive({
      refColorBar: null,
      color: pColor.value || null,
      deg: pDeg.value,
      colors: pColors.value || null,
      selectIndex: 0,
      startMovePst: 0,
      currentMode: type.value, // 当前模式：linear 或 gradient
      shouldEmitOnBarStyleChange: false, // 控制 barStyle 变化时是否触发事件
      lastEmittedStyle: '', // 记录上次发出的样式，用于防止重复触发
      isEmitting: false, // 标志是否正在发出change事件，避免监听自己的更改
    })

    const barStyle = computed(() => {
      if (!state.colors) {
        return ''
      }
      const colors = cloneDeep(state.colors)
        .sort((a, b) => a.pst - b.pst)
        .map((item) => {
          return `${item.color} ${keepDecimal(String(item.pst)||0,5)}%`
        })

      return `linear-gradient(${state.deg}deg, ${colors.join(
        ','
      )});`
    })

    // 拖拽距离
    const { movePst, enableDraggle, resetDraggle } = useDraggle(
      sliderStart,
      sliderMove,
      sliderDone,
    )

    onUnmounted(() => {
      unbindEvents()
    })

    onMounted(() => {
      initColors()
    })

    watch(barStyle, (barStyle) => {
      if (state.currentMode === 'linear') return
      if (!state.shouldEmitOnBarStyleChange) return

      // 防止重复触发：如果样式没有变化，不发出事件
      if (barStyle === state.lastEmittedStyle) return

      state.lastEmittedStyle = barStyle
      emitColorChange({
        style: barStyle,
      })
    })

    watch(
      pColor,
      (pColor) => {
        state.color = cloneDeep(pColor)
      },
      {
        deep: true,
      }
    )

    watch(
      pColors,
      (newPColors, oldPColors) => {
        if (!newPColors) return // 如果 pColors 为空，不更新
        if (state.isEmitting) return // 如果正在发出事件，忽略由此引起的 props 更新

        // 如果颜色数据没有实际变化，不更新（避免重新打开面板时重置）
        if (oldPColors && JSON.stringify(newPColors) === JSON.stringify(oldPColors)) {
          return
        }

        if (state.selectIndex >= newPColors.length) {
          state.selectIndex = newPColors.length - 1
        }
        state.colors = cloneDeep(newPColors)
      },
      {
        deep: true,
      }
    )

    // 监听角度变化，用户修改角度时启用 barStyle 监听
    watch(
      () => state.deg,
      (newDeg, oldDeg) => {
        // 只有在初始化完成后，用户手动修改角度时才启用
        if (oldDeg !== undefined && newDeg !== oldDeg && state.currentMode === 'gradient') {
          state.shouldEmitOnBarStyleChange = true
        }
      }
    )

    function initColors() {
      bindEvents()

      // 初始化颜色值
      if (state.currentMode === 'gradient') {
        // 如果没有传入渐变色数据，使用默认值
        if (!state.colors || state.colors.length === 0) {
          state.colors = [
            {
              color: 'rgba(255, 255, 255, 1)',
              hex: '#ffffff',
              rgba: { r: 255, g: 255, b: 255, a: 1 },
              pst: 0,
            },
            {
              color: 'rgba(0, 0, 0, 1)',
              hex: '#000000',
              rgba: { r: 0, g: 0, b: 0, a: 1 },
              pst: 100,
            },
          ]
        }

        const renderList = cloneDeep(state.colors).sort((a, b) => a.pst - b.pst)
        state.selectIndex = state.colors.findIndex(
          (item) => item.pst === renderList[0].pst
        )

        // 不在初始化时触发事件，避免覆盖传入的初始值
        // 只有用户实际操作（修改颜色、角度、滑块）时才会触发 change 事件
      } else {
        // 如果没有传入纯色数据，使用默认值
        const hasInitialColor = pColor.value !== null && pColor.value !== undefined
        if (!state.color) {
          state.color = {
            hex: '#000000',
            rgba: { r: 0, g: 0, b: 0, a: 1 },
            color: 'rgba(0,0,0,1)',
          }
        }

        // 只有传入了初始值才触发事件
        if (hasInitialColor) {
          emitColorChange({
            color: cloneDeep(state.color),
          })
        }
      }
    }

    function bindEvents() {
      state.currentMode === 'gradient' && window.addEventListener('keyup', handleKeyup)
    }

    function unbindEvents() {
      state.currentMode === 'gradient' &&
        window.removeEventListener('keyup', handleKeyup)
    }

    function handleKeyup(e) {
      if ([8, 46].includes(e.keyCode)) {
        deleteColorPot()
      }
    }

    function deleteColorPot() {
      // 渐变色至少有两点
      if (state.colors.length === 2) return
      state.colors.splice(state.selectIndex, 1)

      // 删完滑块需要重新渲染
      initColors()
    }

    function handlePotBar(e) {
      const barBounding = state.refColorBar.getBoundingClientRect()
      const barLeft = barBounding.left
      const colorPotDist = e.pageX - barLeft

      // 渐变条stopColors
      const rangColors = cloneDeep(state.colors)
        .sort((a, b) => a.pst - b.pst)
        .map((item) => {
          return item.hex
        })

      // 初始化色条Range，用来取渐变色值
      const colorScale = new ColorScale(0, barBounding.width, rangColors)
      const colorPotHex = colorScale.getColor(colorPotDist).toHexString()
      const colorPotRgba = colorScale.getColor(colorPotDist).toRGBAString()
      const colorPotPst = (100 * colorPotDist) / barBounding.width

      state.colors.push({
        color: colorPotRgba,
        hex: colorPotHex,
        pst: colorPotPst,
      })

      // 增加后默认选中
      state.selectIndex = state.colors.length - 1

      // 用户开始交互，启用 barStyle 监听
      state.shouldEmitOnBarStyleChange = true
    }

    function sliderPotDown(index) {
      clickGColorPot(index)
      enableDraggle()
    }

    function sliderStart() {
      state.startMovePst = state.colors[state.selectIndex].pst
    }

    function sliderDone() {
      resetDraggle()
    }

    function sliderMove() {
      const barWidth = state.refColorBar?.getBoundingClientRect().width
      let distRatio =
        ((state.startMovePst * barWidth) / 100 + movePst.value.x) / barWidth

      if (distRatio > 1) {
        distRatio = 1
      } else if (distRatio < 0) {
        distRatio = 0
      }
      state.colors[state.selectIndex].pst = Math.round(distRatio * 100)

      // 用户拖动滑块，启用 barStyle 监听
      state.shouldEmitOnBarStyleChange = true
    }

    function getBarPst(pst) {
      return `calc(${pst}% - 8px)`
    }

    function changeColor(color) {
      const rgba = color.rgba
      const hex = color.hex

      if (state.currentMode === 'linear') {
        const colorValue = `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`
        state.color = {
          rgba,
          hex,
          color: colorValue,
        }

        emitColorChange({
          color: cloneDeep(state.color),
        })
      } else {
        // 用户开始修改渐变色，启用 barStyle 监听
        state.shouldEmitOnBarStyleChange = true
        handleGradientColorChange(color)
      }
    }

    function emitColorChange({
      style,
      colors = cloneDeep(state.colors),
      color = cloneDeep(state.color),
      deg = state.deg,
    }) {
      state.isEmitting = true
      emit('change', {
        style,
        colors,
        color,
        deg,
        mode: state.currentMode, // 返回当前模式
      })
      // 使用 setTimeout 确保 props 更新后再重置标志
      setTimeout(() => {
        state.isEmitting = false
      }, 0)
    }

    function handleGradientColorChange(color) {
      const rgba = color.rgba
      const updatedColor = {
        ...state.colors[state.selectIndex],
        color: `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`,
        hex: color.hex,
        rgba: color.rgba,
      }
      // 使用splice替换整个对象以确保响应式更新
      state.colors.splice(state.selectIndex, 1, updatedColor)
    }

    function clickGColorPot(index) {
      if (state.selectIndex === index) return
      state.selectIndex = index
    }

    function handleClose() {
      emit('close')
    }
    // 切换模式
    function switchMode(mode) {
      if (state.currentMode === mode) return

      // 移除旧模式的事件监听
      unbindEvents()

      state.currentMode = mode

      // 初始化新模式的数据（不触发事件，等用户实际选择后再触发）
      if (mode === 'gradient') {
        if (!state.colors || state.colors.length === 0) {
          state.colors = [
            {
              color: 'rgba(255, 255, 255, 1)',
              hex: '#ffffff',
              rgba: { r: 255, g: 255, b: 255, a: 1 },
              pst: 0,
            },
            {
              color: 'rgba(0, 0, 0, 1)',
              hex: '#000000',
              rgba: { r: 0, g: 0, b: 0, a: 1 },
              pst: 100,
            },
          ]
        }
        state.selectIndex = 0
        // 不触发事件，等用户实际选择颜色后再触发
      } else {
        if (!state.color) {
          state.color = {
            hex: '#000000',
            rgba: { r: 0, g: 0, b: 0, a: 1 },
            color: 'rgba(0,0,0,1)',
          }
        }
        // 不触发事件，等用户实际选择颜色后再触发
      }

      // 绑定新模式的事件监听
      bindEvents()
    }

    return {
      ...toRefs(state),
      changeColor,
      getBarPst,
      barStyle,
      clickGColorPot,
      sliderPotDown,
      handlePotBar,
      handleClose,
      switchMode,
    }
  },
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
@import '~vue-color-kit/dist/vue-color-kit.css';

.color_picker_panel {
  box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.16);
  border-radius: 4px;
  background: #f7f8f9;
  padding: 15px 10px;

  :deep() {
    .hu-color-picker {
      box-shadow: none;
      border-radius: 0;
      background: #f7f8f9;
      padding: 0;
    }
  }

  .gcolor_deg {
    display: block;
    display: flex;
    align-items: center;
    .gcolor_deg_span {
      font-size: 12px;
      margin-right: 10px;
    }
    .number_input {
      min-width: 20px;
      max-width: 20px;
      float: none;
      order: 1;
      margin-left: 10px;
      margin-top: 0;
      display: flex;
      align-items: center;
      text-align: center;
      color: #606266;
      font-size: 12px;
      padding: 3px;
      background-color: #fff;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      &:focus {
        border-color: #409eff;
        outline: 0;
      }
    }
    :deep() {
      .el-input-number__decrease,
      .el-input-number__increase {
        display: none;
      }
      .el-input--mini .el-input__inner {
        padding: 0;
        width: 100% !important;
        height: 22px;
        line-height: 22px;
      }
      .el-slider {
        display: flex;
        flex: 1;
      }
      .el-slider__input {
        min-width: 2em;
        max-width: 2em;
        float: none;
        order: 1;
        margin-left: 10px;
        margin-top: 0;
        display: flex;
        align-items: center;
      }
      .el-slider__runway.show-input {
        margin-right: 0;
        flex: 1;
        order: 0;
        height: 3px;
      }
      .el-slider__button {
        width: 13px;
        height: 13px;
        border-color: #fff;
        box-shadow: 0 0 4px rgba($color: #000, $alpha: 0.4);
      }
      .el-slider__bar {
        height: 3px;
      }
      .el-slider__button-wrapper {
        top: -17px;
      }
    }
  }

  .color_hd {
    margin-bottom: 15px;
  }
  .color_hd_0 {
    margin-bottom: 0;
  }

  // 模式切换按钮样式
  .mode_switch {
    display: flex;
    gap: 8px;
    margin: 10px 0;
    padding: 4px;
    background: #fff;
    border-radius: 4px;

    .mode_btn {
      flex: 1;
      padding: 6px 12px;
      border: none;
      background: transparent;
      color: #606266;
      font-size: 13px;
      cursor: pointer;
      border-radius: 3px;
      transition: all 0.2s;

      &:hover {
        background: #f5f7fa;
      }

      &.active {
        background: #409eff;
        color: #fff;
      }
    }
  }

  .title {
    font-size: 16px;
    display: flex;
    justify-content: space-between;
    .close_box {
      speak: none;
      font-style: normal;
      font-weight: 400;
      font-variant: normal;
      text-transform: none;
      line-height: 1;
      vertical-align: baseline;
      display: inline-block;
      color: #909399;
      width: 26px;
      height: 100%;
      font-size: 21px;
      cursor: pointer;
    }
  }
  .gcolor {
    position: relative;
    margin: 10px 0 20px;
  }
  .gcolor_bar {
    position: relative;
    margin-top: 3px;
    height: 16px;
    background: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAYAAADED76LAAAAJ0lEQVQoU2M8c/X2fwYkYKylgsxlYKSDgv///6O44ey1O6huoL0CAJgaKeXe+C99AAAAAElFTkSuQmCC')
      repeat;
    border-radius: 2px;
  }
  .gcolor_bar_bg {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }
  .gcolor_bar_pot_box {
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    z-index: 1;
    width: calc(100% - 16px);
    pointer-events: none;
  }
  .gcolor_bar_pot {
    pointer-events: initial;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 16px;
    width: 12px;
    cursor: pointer;
    border: 2px solid #fff;
    &.on {
      &:before {
        visibility: visible;
      }
      z-index: 2;
    }
    &:after {
      content: '';
      position: absolute;
      left: -3px;
      right: -3px;
      top: -3px;
      bottom: -3px;
      border: 1px solid #999;
      border-radius: 3px;
    }
    &:before {
      content: '';
      position: absolute;
      left: -4px;
      right: -4px;
      top: -4px;
      bottom: -4px;
      border: 2px solid var(--hoverColor);
      visibility: hidden;
      z-index: 1;
      border-radius: 3px;
    }
  }
}
</style>
